import { parseBahtToSatang } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { controlClass } from '../../components/fieldControl';
import { Input } from '../../components/Input';
import {
  listBranches,
  listBranchPricing,
  listCategories,
  listProducts,
} from '../../data/fetchers';
import { upsertBranchPricing } from '../../data/mutators';
import { fmtAmount } from '../../lib/format';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { ProductHeader } from './ProductHeader';

interface PriceChange {
  productId: string;
  branchId: string;
  price: number | null;
}
const cellKey = (productId: string, branchId: string) =>
  JSON.stringify([productId, branchId]);
export function BranchPricingView() {
  const qc = useQueryClient(),
    user = useSession((s) => s.user),
    canEdit = !!user && canEditMasterData(user);
  const productQuery = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const branchQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const pricingQuery = useQuery({
    queryKey: ['pricing'],
    queryFn: listBranchPricing,
  });
  const categoryQuery = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const products = productQuery.data ?? [],
    branches = branchQuery.data ?? [],
    pricing = pricingQuery.data ?? [],
    categories = categoryQuery.data ?? [];
  const [search, setSearch] = useState(''),
    [category, setCategory] = useState(''),
    [branch, setBranch] = useState(''),
    [customOnly, setCustomOnly] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({}),
    [saved, setSaved] = useState(false);
  const pending =
    productQuery.isPending ||
    branchQuery.isPending ||
    pricingQuery.isPending ||
    categoryQuery.isPending;
  const error =
    productQuery.error ||
    branchQuery.error ||
    pricingQuery.error ||
    categoryQuery.error;
  const overrideOf = (id: string, bid: string) =>
    pricing.find((p) => p.productId === id)?.prices[bid];
  const persisted = (id: string, bid: string) => {
    const value = overrideOf(id, bid);
    return value == null ? '' : String(value / 100);
  };
  const valueOf = (id: string, bid: string) =>
    drafts[cellKey(id, bid)] ?? persisted(id, bid);
  const changes: PriceChange[] = [];
  const invalid = new Set<string>();
  for (const [key, raw] of Object.entries(drafts)) {
    const [productId, branchId] = JSON.parse(key) as [string, string];
    const trimmed = raw.trim();
    let price: number | null = null;
    if (trimmed !== '') {
      if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
        invalid.add(key);
        continue;
      }
      try {
        price = parseBahtToSatang(trimmed);
        if (!Number.isSafeInteger(price) || price < 0) {
          invalid.add(key);
          continue;
        }
      } catch {
        invalid.add(key);
        continue;
      }
    }
    if (price !== (overrideOf(productId, branchId) ?? null))
      changes.push({ productId, branchId, price });
  }
  const dirtyKeys = new Set(
    changes.map((c) => cellKey(c.productId, c.branchId)),
  );
  const mutation = useMutation({
    mutationFn: async (list: PriceChange[]) => {
      let rows = pricing;
      for (const c of list) {
        rows = await upsertBranchPricing(c.productId, c.branchId, c.price);
        // Keep successful cells committed even if a later request fails.
        qc.setQueryData(['pricing'], rows);
        setDrafts((old) => {
          const next = { ...old };
          delete next[cellKey(c.productId, c.branchId)];
          return next;
        });
        void qc.invalidateQueries({ queryKey: ['report'] });
      }
      return rows;
    },
    onSuccess: () => {
      setSaved(true);
    },
  });
  const shownBranches = branches.filter((b) => !branch || b.id === branch);
  const shownProducts = products.filter(
    (p) =>
      (p.name + ' ' + p.id)
        .toLowerCase()
        .includes(search.trim().toLowerCase()) &&
      (!category || p.categoryId === category) &&
      (!customOnly ||
        shownBranches.some((b) => valueOf(p.id, b.id).trim() !== '')),
  );
  const save = () => {
    if (
      canEdit &&
      !pending &&
      !error &&
      !invalid.size &&
      changes.length &&
      !mutation.isPending
    )
      mutation.mutate(changes);
  };
  const status = mutation.isPending
    ? 'กำลังบันทึกราคา'
    : mutation.error
      ? 'บันทึกไม่ครบ กรุณาลองใหม่'
      : invalid.size
        ? 'กรุณาตรวจสอบช่องราคา'
        : changes.length
          ? changes.length + ' ช่องรอบันทึก'
          : saved
            ? 'บันทึกราคาแล้ว'
            : 'ไม่มีราคาที่รอบันทึก';
  return (
    <div className="bo-fade">
      <ProductHeader
        title="ราคาแยกสาขา"
        subtitle="กำหนดราคาขายต่อหน่วยฐานของแต่ละสาขา โดยอ้างอิงราคากลางของสินค้า"
      />
      <div className="bo-card mb-4 grid gap-3 p-4 md:grid-cols-[minmax(180px,1fr)_200px_200px]">
        <Field label="ค้นหาสินค้า">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาด้วยชื่อหรือรหัสสินค้า"
          />
        </Field>
        <Field label="หมวดหมู่">
          <select
            className={controlClass()}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">ทุกหมวดหมู่</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="สาขาที่แสดง">
          <select
            className={controlClass()}
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          >
            <option value="">ทุกสาขา</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.short}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm-label">
        <span className="font-semibold">
          {pending
            ? 'กำลังโหลดราคา'
            : `แสดง ${shownProducts.length} สินค้า · ${shownBranches.length} สาขา`}
        </span>
        <label className="flex min-h-11 items-center gap-2">
          <input
            type="checkbox"
            checked={customOnly}
            onChange={(e) => setCustomOnly(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          เฉพาะสินค้าที่มีราคาเฉพาะสาขา
        </label>
      </div>
      <p className="mb-4 rounded-card border border-line bg-surface-2 p-4 text-sm-label text-ink-2">
        เว้นว่างเพื่อใช้ราคากลาง · ใส่ตัวเลขเพื่อกำหนดราคาเฉพาะสาขา ·
        ราคาทั้งหมดเป็นบาทต่อหน่วยฐาน
      </p>
      {mutation.error && (
        <p role="alert" className="mb-4 text-danger">
          {mutation.error instanceof Error
            ? mutation.error.message
            : 'บันทึกราคาไม่สำเร็จ'}{' '}
          · ช่องที่บันทึกสำเร็จแล้วจะไม่ถูกส่งซ้ำ
        </p>
      )}
      {invalid.size > 0 && (
        <p role="alert" className="mb-4 text-danger">
          ราคาต้องไม่ติดลบ และมีทศนิยมไม่เกิน 2 ตำแหน่ง
        </p>
      )}
      {error ? (
        <div role="alert" className="bo-card p-5">
          โหลดราคาไม่สำเร็จ{' '}
          <Btn
            onClick={() => {
              void productQuery.refetch();
              void branchQuery.refetch();
              void pricingQuery.refetch();
              void categoryQuery.refetch();
            }}
          >
            ลองใหม่
          </Btn>
        </div>
      ) : pending ? (
        <div
          role="status"
          className="h-32 animate-pulse rounded-card bg-line-2"
        >
          กำลังโหลดราคาแยกสาขา
        </div>
      ) : (
        <div className="bo-card overflow-x-auto">
          <table className="bo-table">
            <thead>
              <tr>
                <th className="min-w-[220px]">สินค้า / หน่วยฐาน</th>
                <th className="!text-right">ราคากลาง</th>
                {shownBranches.map((b) => (
                  <th key={b.id} className="!text-right">
                    {b.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownProducts.map((p) => {
                const unit =
                  p.units.find((u) => u.id === p.baseUnitId) ??
                  p.units.find((u) => u.factor === 1);
                const base = unit?.price;
                return (
                  <tr key={p.id}>
                    <td>
                      <span className="font-semibold">{p.name}</span>
                      <span className="block text-xs text-ink-3">
                        {p.id} · {p.baseUnit}
                      </span>
                    </td>
                    <td className="num !text-right">
                      {base == null ? '—' : fmtAmount(base)}
                    </td>
                    {shownBranches.map((b) => {
                      const key = cellKey(p.id, b.id),
                        dirty = dirtyKeys.has(key),
                        custom = valueOf(p.id, b.id).trim() !== '';
                      return (
                        <td key={b.id}>
                          <div className="ml-auto flex min-w-[138px] max-w-[180px] flex-col items-end gap-1">
                            {canEdit ? (
                              <input
                                aria-label={`ราคา ${p.id} ${b.id}`}
                                aria-invalid={invalid.has(key)}
                                type="number"
                                min="0"
                                step="0.01"
                                disabled={mutation.isPending}
                                value={valueOf(p.id, b.id)}
                                placeholder={
                                  base == null ? '' : fmtAmount(base)
                                }
                                onChange={(e) => {
                                  setDrafts((old) => ({
                                    ...old,
                                    [key]: e.target.value,
                                  }));
                                  setSaved(false);
                                  mutation.reset();
                                }}
                                className={controlClass(
                                  'md',
                                  `num w-full text-right ${invalid.has(key) ? '!border-danger' : dirty ? '!border-primary !bg-primary-light/30' : ''}`,
                                )}
                              />
                            ) : (
                              <span className="num">
                                {overrideOf(p.id, b.id) != null
                                  ? fmtAmount(overrideOf(p.id, b.id)!)
                                  : base == null
                                    ? '—'
                                    : fmtAmount(base)}
                              </span>
                            )}
                            <span
                              className={`text-xs ${dirty ? 'text-primary' : 'text-ink-3'}`}
                            >
                              {invalid.has(key)
                                ? 'ราคาไม่ถูกต้อง'
                                : dirty
                                  ? custom
                                    ? 'แก้ไขแล้ว · รอบันทึก'
                                    : 'กลับใช้ราคากลาง · รอบันทึก'
                                  : custom
                                    ? 'ราคาเฉพาะสาขา'
                                    : 'ใช้ราคากลาง'}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {!shownProducts.length && (
                <tr>
                  <td
                    colSpan={2 + shownBranches.length}
                    className="!py-8 !text-center"
                  >
                    ไม่พบสินค้าที่ตรงกับตัวกรอง
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <div className="sticky -bottom-4 z-10 -mx-4 -mb-4 mt-5 border-t border-line bg-surface px-4 py-3 shadow-[0_-8px_24px_-16px_rgba(39,38,35,0.25)] sm:-bottom-6 sm:-mx-6 sm:-mb-6 sm:px-6 xl:-bottom-8 xl:-mx-8 xl:-mb-8 xl:px-8">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
            <p
              role="status"
              className="hidden min-w-0 items-center gap-2 text-sm-label text-ink-2 sm:flex"
            >
              <span
                aria-hidden="true"
                className={`h-2 w-2 shrink-0 rounded-full ${invalid.size || mutation.error ? 'bg-danger' : changes.length || mutation.isPending ? 'bg-warn' : 'bg-ok'}`}
              />
              <span className="truncate">{status}</span>
            </p>
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
              <Btn
                className="w-full sm:w-auto sm:min-w-24"
                disabled={mutation.isPending || !Object.keys(drafts).length}
                onClick={() => {
                  setDrafts({});
                  setSaved(false);
                  mutation.reset();
                }}
              >
                ยกเลิก
              </Btn>
              <Btn
                kind="primary"
                className="w-full sm:w-auto sm:min-w-32"
                disabled={
                  pending ||
                  !!error ||
                  !changes.length ||
                  !!invalid.size ||
                  mutation.isPending
                }
                onClick={save}
              >
                บันทึกราคา
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
